import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { Navigate, Route, Routes, useNavigate, useParams, useSearchParams } from "react-router-dom";
import type { ParticipantState, SurveyAnswers, SurveyAudience } from "@exp/shared";
import { CONTACT_EMAIL, emptySurveyAnswers, normalizeSurveyAnswers, surveyAiUsesAnswered, surveyAnswersComplete, surveyQuestionCount, surveyShowsFollowUps } from "@exp/shared";
import {
  api,
  currentPhaseElapsed,
  fetchState,
  getStoredToken,
  joinByCode,
  joinByLink,
  notePhaseClock,
  queueEvent,
  scheduleFlush,
  storageBlocked,
  storeToken,
  syncOutbox,
} from "./api";
import { enqueue } from "./outbox";
import { applyParticipantState, isReadingPhase, switchDocument } from "./tracking/applyState";
import { closeReadingVisit, detachReadingVisit, openReadingVisit, type Via } from "./tracking/reading";
import {
  formatMissingDecisionHint,
  isDecisionComplete,
  seedForDecision,
  type DecisionAnswerFields,
} from "./decisionCompleteness";
import { splitDecisionPrompt } from "./decisionPrompt";
import { t } from "./i18n";
import { phaseRemainingMs } from "./phaseClock";

function useTabVisible() {
  const [visible, setVisible] = useState(document.visibilityState === "visible");
  useEffect(() => {
    const onVis = () => {
      const v = document.visibilityState === "visible";
      setVisible(v);
      void queueEvent("visibility", { visible: v });
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);
  return visible;
}

function formatMs(ms: number | null): string {
  if (ms == null) return "—";
  const s = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

function Footer({ code, remaining }: { code?: string; remaining?: number | null }) {
  return (
    <footer className="footer">
      <span>{code ? `Code: ${code}` : " "}</span>
      <span className="timer">{formatMs(remaining ?? null)}</span>
    </footer>
  );
}

function JoinPage() {
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const nav = useNavigate();
  return (
    <div className="shell">
      <h1>{t("joinTitle")}</h1>
      <p>{t("joinHint")}</p>
      {storageBlocked() && <div className="banner">{t("storageBlocked")}</div>}
      <label>
        {t("codeLabel")}
        <input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          style={{ display: "block", width: "100%", margin: "0.5rem 0 1rem", padding: "0.6rem", font: "inherit" }}
        />
      </label>
      {err && <p style={{ color: "crimson" }}>{err}</p>}
      <button
        type="button"
        onClick={async () => {
          try {
            const result = await joinByCode(code.trim());
            if (result.kind === "workshop" && result.link_token) nav(`/w/${result.link_token}`);
            else nav("/p");
          } catch (e: any) {
            setErr(e.message);
          }
        }}
      >
        {t("joinButton")}
      </button>
    </div>
  );
}

function LinkJoin() {
  const { token } = useParams();
  const nav = useNavigate();
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!token) return;
    joinByLink(token)
      .then(() => nav("/p", { replace: true }))
      .catch((e) => setErr(e.message));
  }, [token, nav]);
  return (
    <div className="shell">
      {err ? <p>{err}</p> : <p>Joining…</p>}
    </div>
  );
}

function BlankSlider({
  value,
  touched,
  onChange,
  optionA,
  optionB,
}: {
  value: number | null;
  touched: boolean;
  onChange: (v: number) => void;
  optionA: string;
  optionB: string;
}) {
  const track = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const [visual, setVisual] = useState<number | null>(touched && value != null ? value : null);

  useEffect(() => {
    if (dragging.current) return;
    if (touched && value != null) setVisual(value);
    else setVisual(null);
  }, [value, touched]);

  const rawFromClientX = (clientX: number) => {
    const el = track.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    const w = Math.max(1, rect.width);
    return Math.max(0, Math.min(100, ((clientX - rect.left) / w) * 100));
  };

  const applyPointer = (clientX: number) => {
    const raw = rawFromClientX(clientX);
    setVisual(raw);
    onChange(Math.round(raw));
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dragging.current = true;
    applyPointer(e.clientX);
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return;
    applyPointer(e.clientX);
  };
  const onPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return;
    dragging.current = false;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
    const raw = rawFromClientX(e.clientX);
    const snapped = Math.round(raw);
    setVisual(snapped);
    onChange(snapped);
  };

  const show = visual != null;
  const thumbPct = visual ?? 0;
  const pctA = show ? Math.round(thumbPct) : null;
  const pctB = pctA != null ? 100 - pctA : null;
  const ticks = Array.from({ length: 21 }, (_, i) => i * 5);

  return (
    <div className="slider-wrap">
      <div className="slider-ends" aria-hidden="true">
        <span className="slider-end-a">{optionA}</span>
        <span className="slider-end-b">{optionB}</span>
      </div>
      <div
        className="slider-hit"
        ref={track}
        role="slider"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={touched && value != null ? value : undefined}
        aria-label={`${optionA} vs ${optionB} percent`}
        tabIndex={0}
        style={{ touchAction: "none" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onKeyDown={(e) => {
          const cur = touched && value != null ? value : 50;
          if (e.key === "ArrowLeft") {
            e.preventDefault();
            onChange(Math.max(0, cur - 1));
          }
          if (e.key === "ArrowRight") {
            e.preventDefault();
            onChange(Math.min(100, cur + 1));
          }
        }}
      >
        <div className="slider-track">
          {show ? (
            <>
              <div className="slider-seg-a" style={{ width: `${thumbPct}%` }} />
              <div className="slider-seg-b" style={{ width: `${100 - thumbPct}%` }} />
            </>
          ) : (
            <div className="slider-seg-unset" />
          )}
        </div>
        {show && <div className="slider-handle" style={{ left: `${thumbPct}%` }} />}
      </div>
      <div className="slider-ticks" aria-hidden="true">
        {ticks.map((t) => {
          const major = t % 10 === 0;
          return (
            <span
              key={t}
              className={`slider-tick ${major ? "major" : "minor"}`}
              data-edge={t === 0 ? "start" : t === 100 ? "end" : undefined}
              style={{ left: `${t}%` }}
            >
              <i />
              {major ? <em>{t}</em> : null}
            </span>
          );
        })}
      </div>
      <div className="amounts">
        <span>
          {pctA != null
            ? `${optionA} ${pctA}% · ${optionB} ${pctB}%`
            : `${optionA} —% · ${optionB} —%`}
        </span>
      </div>
    </div>
  );
}

function readingScroller(docEl: HTMLElement): HTMLElement | "page" {
  const panel = docEl.closest(".reading-main");
  if (panel instanceof HTMLElement && getComputedStyle(panel).overflowY === "auto") return panel;
  return "page";
}

function readReadingScroll(docEl: HTMLElement): number {
  const scroller = readingScroller(docEl);
  return scroller === "page" ? window.scrollY : scroller.scrollTop;
}

function writeReadingScroll(docEl: HTMLElement, top: number): void {
  const scroller = readingScroller(docEl);
  if (scroller === "page") {
    const panel = docEl.closest(".reading-main");
    if (panel instanceof HTMLElement) panel.scrollTop = 0;
    window.scrollTo(0, top);
    return;
  }
  scroller.scrollTop = top;
  window.scrollTo(0, 0);
}

function Experiment() {
  const [search] = useSearchParams();
  const tokenParam = search.get("token");
  const [state, setState] = useState<ParticipantState | null>(null);
  const stateRef = useRef<ParticipantState | null>(null);
  stateRef.current = state;
  const [error, setError] = useState("");
  const [docIndex, setDocIndex] = useState(0);
  const currentDocNavRef = useRef<HTMLButtonElement | null>(null);
  const scrollDocNavIntoViewRef = useRef(false);
  const [share, setShare] = useState<number | null>(null);
  const [touched, setTouched] = useState(false);
  const [confidence, setConfidence] = useState<number | null>(null);
  const [confidenceTouched, setConfidenceTouched] = useState(false);
  const [justification, setJustification] = useState("");
  const [revision, setRevision] = useState(1);
  const [survey, setSurvey] = useState<SurveyAnswers>(emptySurveyAnswers());
  const [surveyRevision, setSurveyRevision] = useState(1);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [submitHint, setSubmitHint] = useState("");
  const [remaining, setRemaining] = useState<number | null>(null);
  const [advanceStuck, setAdvanceStuck] = useState(false);
  const visible = useTabVisible();
  const online = typeof navigator !== "undefined" ? navigator.onLine : true;
  const [isOnline, setOnline] = useState(online);
  const advanceAbortRef = useRef<{ cancelled: boolean } | null>(null);
  const openViaRef = useRef<Via>("resume");
  const navIntentRef = useRef<{ via: Via; from: string | null } | null>(null);
  const docElRef = useRef<HTMLDivElement | null>(null);
  const phaseClockKeyRef = useRef("");
  const phaseClockRef = useRef<{ entered: number | null; serverNow: number; syncedAt: number } | null>(
    null,
  );
  const appliedDecisionKey = useRef<string | null | undefined>(undefined);
  const docScrollRef = useRef(new Map<string, number>());
  const scrollPhaseRef = useRef<string | null>(null);
  const scrollDocRef = useRef<string | null>(null);
  const decisionKey = state?.decision?.key ?? null;
  if (appliedDecisionKey.current !== decisionKey) {
    appliedDecisionKey.current = decisionKey;
    const seed = seedForDecision(state?.decision ?? null);
    setConfirmSubmit(false);
    setSubmitHint("");
    setShare(seed.share);
    setTouched(seed.touched);
    setConfidence(seed.confidence);
    setConfidenceTouched(seed.confidenceTouched);
    setJustification(seed.justification);
    setRevision(seed.revision);
  }

  function applyState(next: ParticipantState) {
    const prev = stateRef.current;
    applyParticipantState({
      prev,
      next,
      phaseOf: (s) => s.phase,
      closeVisit: () => closeReadingVisit(true),
      setState: (s) => {
        if (prev && isReadingPhase(s.phase) && prev.phase !== s.phase) openViaRef.current = "start";
        if (!prev || prev.phase !== s.phase) setDocIndex(0);
        stateRef.current = s;
        setState(s);
      },
      scheduleFlush,
    });
  }

  useEffect(() => {
    setDocIndex(0);
  }, [state?.phase]);

  useEffect(() => {
    if (tokenParam) storeToken(tokenParam);
  }, [tokenParam]);

  const refresh = useCallback(async () => {
    try {
      await syncOutbox();
      const s = await fetchState(tokenParam ?? getStoredToken());
      applyState(s);
      setError("");
      if (s.decision) {
        const seed = seedForDecision(s.decision);
        setShare(seed.share);
        setTouched(seed.touched);
        setConfidence(seed.confidence);
        setConfidenceTouched(seed.confidenceTouched);
        setJustification(seed.justification);
        setRevision(seed.revision);
      }
      setConfirmSubmit(false);
      setSubmitHint("");
      if (s.survey) {
        setSurvey(
          normalizeSurveyAnswers(
            s.survey.draft ?? emptySurveyAnswers(),
            s.survey.audience ?? "board",
          ),
        );
        setSurveyRevision(s.survey.revision);
      }
    } catch (e: any) {
      setError(e.message);
    }
  }, [tokenParam]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // The deadline keeps running while the tab is hidden. Ask the server again when
  // the tab is shown: if the phase has ended, the server moves them on.
  useEffect(() => {
    const onShow = () => {
      if (document.visibilityState === "hidden") return;
      void refresh();
    };
    document.addEventListener("visibilitychange", onShow);
    window.addEventListener("pageshow", onShow);
    return () => {
      document.removeEventListener("visibilitychange", onShow);
      window.removeEventListener("pageshow", onShow);
    };
  }, [refresh]);

  useEffect(() => {
    const onOnline = () => {
      setOnline(true);
      void syncOutbox().then(refresh);
    };
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [refresh]);

  // Countdown from the phase deadline. The server clock is sampled once per state
  // payload. Hiding the tab must not take a new sample: Safari pauses the interval
  // while the tab is in the background, and pairing the old server time with a new
  // Date.now() would put the full phase time back on screen.
  const phaseClockKey = state?.server_now ? `${state.phase_entered_at ?? ""}|${state.server_now}` : "";
  if (phaseClockKey && phaseClockKeyRef.current !== phaseClockKey && state?.server_now) {
    phaseClockKeyRef.current = phaseClockKey;
    const entered = state.phase_entered_at ? new Date(state.phase_entered_at).getTime() : null;
    const serverNow = new Date(state.server_now).getTime();
    const syncedAt = Date.now();
    phaseClockRef.current = { entered, serverNow, syncedAt };
    notePhaseClock(entered, serverNow, syncedAt);
  }

  useEffect(() => {
    if (!state?.server_now) return;
    const pair = phaseClockRef.current;
    const serverNow = pair?.serverNow ?? new Date(state.server_now).getTime();
    const syncedAt = pair?.syncedAt ?? Date.now();
    notePhaseClock(
      state.phase_entered_at ? new Date(state.phase_entered_at).getTime() : null,
      serverNow,
      syncedAt,
    );
    if (!state.phase_deadline_at) {
      setRemaining(null);
      return;
    }
    const deadline = new Date(state.phase_deadline_at).getTime();
    const tick = () => {
      setRemaining(phaseRemainingMs(deadline, serverNow, syncedAt, Date.now()));
    };
    let id = window.setInterval(tick, 250);
    tick();
    const wake = () => {
      if (document.visibilityState === "hidden") return;
      window.clearInterval(id);
      tick();
      id = window.setInterval(tick, 250);
    };
    document.addEventListener("visibilitychange", wake);
    window.addEventListener("pageshow", wake);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", wake);
      window.removeEventListener("pageshow", wake);
    };
  }, [state?.phase_deadline_at, state?.server_now, state?.phase_entered_at]);

  const advanceReading = useCallback(async (ctl: { cancelled: boolean }, phase: string) => {
    const READING = ["base_brief", "ai_brief", "intervention"];
    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
    let attempts = 0;
    while (!ctl.cancelled) {
      attempts += 1;
      try {
        const s = await api<ParticipantState>("/api/participant/advance", {
          method: "POST",
          body: JSON.stringify({
            client_time: new Date().toISOString(),
            tab_visible: true,
            phase,
          }),
        });
        if (!ctl.cancelled) {
          setAdvanceStuck(false);
          applyState(s);
        }
        return;
      } catch (e: any) {
        if (ctl.cancelled) return;
        const msg = e?.message ?? "";
        if (msg === "not_in_core_phase" || msg === "no_visit") {
          try {
            const s = await fetchState();
            if (!ctl.cancelled && s && !READING.includes(s.phase as string)) {
              setAdvanceStuck(false);
              applyState(s);
              return;
            }
          } catch {
            /* keep retrying */
          }
        }
        if (attempts >= 8) {
          setAdvanceStuck(true);
          return;
        }
        await sleep(1000);
      }
    }
  }, []);

  // Auto-advance reading / timeout decisions — only after remaining is synced for this phase
  const phaseKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!state?.phase_deadline_at) return;
    if (phaseKeyRef.current !== state.phase) {
      phaseKeyRef.current = state.phase;
      setAdvanceStuck(false);
      return; // wait for next tick with fresh remaining
    }
    if (remaining == null || remaining > 0) return;
    if (!visible) return;
    const phase = state.phase;
    if (["base_brief", "ai_brief", "intervention"].includes(phase)) {
      if (advanceAbortRef.current) return;
      const ctl = { cancelled: false };
      advanceAbortRef.current = ctl;
      void advanceReading(ctl, phase).finally(() => {
        if (advanceAbortRef.current === ctl) advanceAbortRef.current = null;
      });
      return () => {
        ctl.cancelled = true;
        if (advanceAbortRef.current === ctl) advanceAbortRef.current = null;
      };
    }
    if (state.decision && ["d1", "d2", "d3"].includes(phase)) {
      void submitDecision("timeout");
    }
    if (phase === "survey") {
      void submitSurvey("timeout");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining, visible, state?.phase, state?.phase_deadline_at, advanceReading]);

  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (state && state.phase !== "end" && !state.session_ended) {
        e.preventDefault();
        e.returnValue = t("leaveWarn");
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    // Prevent back navigation changing phases
    history.pushState(null, "", location.href);
    const onPop = () => history.pushState(null, "", location.href);
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("popstate", onPop);
    };
  }, [state]);

  const surveyPhaseEntered = useRef(false);
  useEffect(() => {
    if (state?.phase === "survey") {
      if (!surveyPhaseEntered.current) {
        surveyPhaseEntered.current = true;
        if (state.survey) {
          setSurvey(
            normalizeSurveyAnswers(
              state.survey.draft ?? emptySurveyAnswers(),
              state.survey.audience ?? "board",
            ),
          );
          setSurveyRevision(state.survey.revision);
        }
      }
    } else {
      surveyPhaseEntered.current = false;
    }
  }, [state?.phase, state?.survey]);

  // Draft autosave
  useEffect(() => {
    if (!state?.decision) return;
    const id = setTimeout(() => {
      void api("/api/participant/draft", {
        method: "POST",
        body: JSON.stringify({
          decision_key: state.decision!.key,
          share_a: share,
          slider_touched: touched,
          confidence,
          confidence_touched: confidenceTouched,
          justification,
          revision,
        }),
      });
      void queueEvent(
        "draft_save",
        { revision, justification_length: justification.length },
        state.phase,
      );
    }, 2000);
    return () => clearTimeout(id);
  }, [
    share,
    touched,
    confidence,
    confidenceTouched,
    justification,
    revision,
    state?.decision?.key,
    state?.phase,
  ]);

  useEffect(() => {
    if (state?.phase !== "survey") return;
    const id = setTimeout(() => {
      void api("/api/participant/survey/draft", {
        method: "POST",
        body: JSON.stringify({
          answers: normalizeSurveyAnswers(survey, state?.survey?.audience ?? "board"),
          revision: surveyRevision,
        }),
      });
    }, 2000);
    return () => clearTimeout(id);
  }, [survey, surveyRevision, state?.phase, state?.survey?.audience]);

  const docs = state?.documents ?? state?.visible_docs ?? [];
  const safeDocIndex = docs.length > 0 ? Math.min(docIndex, docs.length - 1) : 0;
  const activeDoc = docs[safeDocIndex];
  const showDocNav = docs.length > 1;
  const readingPhase =
    !!state && ["base_brief", "ai_brief", "intervention"].includes(state.phase);

  useLayoutEffect(() => {
    if (!readingPhase || !activeDoc || !docElRef.current || !state) return;
    if (scrollPhaseRef.current !== state.phase) {
      docScrollRef.current.clear();
      scrollPhaseRef.current = state.phase;
      scrollDocRef.current = null;
    }
    if (scrollDocRef.current !== activeDoc.id) {
      writeReadingScroll(docElRef.current, docScrollRef.current.get(activeDoc.id) ?? 0);
      scrollDocRef.current = activeDoc.id;
    }
    const intent = navIntentRef.current;
    navIntentRef.current = null;
    const via = intent?.via ?? openViaRef.current;
    openViaRef.current = "resume";
    try {
      openReadingVisit({
        docRoot: docElRef.current,
        docCount: docs.length,
        documentId: activeDoc.id,
        via,
        fromDocumentId: intent?.from ?? null,
        phaseKey: state.phase,
        phaseElapsedMs: currentPhaseElapsed() ?? 0,
      });
    } catch {
      /* tracking must not block reading */
    }
    return () => {
      try {
        detachReadingVisit();
      } catch {
        /* tracking */
      }
    };
  }, [readingPhase, state?.phase, activeDoc?.id, docs.length]);

  useEffect(() => {
    if (!scrollDocNavIntoViewRef.current) return;
    scrollDocNavIntoViewRef.current = false;
    const item = currentDocNavRef.current;
    const list = item?.closest(".doc-nav-list");
    if (!(item instanceof HTMLElement) || !(list instanceof HTMLElement)) return;
    const top = item.offsetTop;
    const bottom = top + item.offsetHeight;
    if (top < list.scrollTop) list.scrollTop = top;
    else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight;
  }, [docIndex]);

  function goToDoc(index: number, via: Via) {
    if (index === safeDocIndex) return;
    if (docElRef.current && activeDoc) {
      docScrollRef.current.set(activeDoc.id, readReadingScroll(docElRef.current));
    }
    const from = activeDoc?.id ?? null;
    switchDocument({
      closeVisit: () => closeReadingVisit(true),
      applyIndex: () => {
        navIntentRef.current = { via, from };
        if (via !== "list") scrollDocNavIntoViewRef.current = true;
        setDocIndex(index);
      },
    });
    try {
      scheduleFlush();
    } catch {
      /* tracking must not block navigation */
    }
  }

  function decisionAnswerFields(): DecisionAnswerFields | null {
    if (!state?.decision) return null;
    return {
      sliderTouched: touched,
      confidenceTouched,
      confidence,
      justification,
      maxChars: state.decision.max_chars,
    };
  }

  function tryShowConfirm(): void {
    const fields = decisionAnswerFields();
    if (!fields) return;
    if (!isDecisionComplete(fields)) {
      setSubmitHint(formatMissingDecisionHint(fields));
      setConfirmSubmit(false);
      return;
    }
    setSubmitHint("");
    setConfirmSubmit(true);
    void queueEvent("decision_confirm", { action: "open" }, state?.phase);
  }

  function tryConfirmSubmit(): void {
    const fields = decisionAnswerFields();
    if (!fields) return;
    if (!isDecisionComplete(fields)) {
      setSubmitHint(formatMissingDecisionHint(fields));
      return;
    }
    setSubmitHint("");
    void submitDecision("explicit");
  }

  async function submitDecision(reason: "explicit" | "timeout") {
    if (!state?.decision) return;
    const attempt_id = crypto.randomUUID();
    const body = {
      attempt_id,
      decision_key: state.decision.key,
      share_a: touched ? share : null,
      slider_touched: touched,
      confidence: confidenceTouched ? confidence : null,
      confidence_touched: confidenceTouched,
      justification,
      reason,
      client_time: new Date().toISOString(),
      draft_revision: revision,
    };
    try {
      const s = await api<ParticipantState>("/api/participant/decision", {
        method: "POST",
        body: JSON.stringify(body),
      });
      applyState(s);
    } catch {
      await enqueue({
        id: attempt_id,
        priority: 1,
        path: "/api/participant/decision",
        method: "POST",
        body,
        created_at: Date.now(),
      });
    }
  }

  async function submitSurvey(reason: "explicit" | "timeout") {
    const attempt_id = crypto.randomUUID();
    const answers = normalizeSurveyAnswers(survey, state?.survey?.audience ?? "board");
    const body = {
      attempt_id,
      answers,
      reason,
      client_time: new Date().toISOString(),
      draft_revision: surveyRevision,
    };
    try {
      const s = await api<ParticipantState>("/api/participant/survey", {
        method: "POST",
        body: JSON.stringify(body),
      });
      applyState(s);
    } catch {
      await enqueue({
        id: attempt_id,
        priority: 1,
        path: "/api/participant/survey",
        method: "POST",
        body,
        created_at: Date.now(),
      });
    }
  }

  if (error && !state) {
    return (
      <div className="shell">
        <p>{error}</p>
        <a href="/join">Join</a>
      </div>
    );
  }
  if (!state) return <div className="shell">Loading…</div>;

  return (
    <>
      <div className="shell">
        {!isOnline && <div className="banner">{t("offline")}</div>}
        {storageBlocked() && <div className="banner">{t("storageBlocked")}</div>}

        {state.session_ended && (
          <div>
            <h1>{t("sessionEnded")}</h1>
            <p>Public code: {state.public_code}</p>
          </div>
        )}

        {state.phase === "consent" && (
          <div>
            <div className="doc" dangerouslySetInnerHTML={{ __html: state.documents?.[0]?.html ?? "" }} />
            <p style={{ marginTop: "1rem" }}>
              <button
                type="button"
                onClick={async () => {
                  const s = await api<ParticipantState>("/api/participant/consent", {
                    method: "POST",
                    body: JSON.stringify({ consent_version: "1" }),
                  });
                  applyState(s);
                }}
              >
                {t("consentContinue")}
              </button>
            </p>
          </div>
        )}

        {state.phase === "instructions" && (
          <div>
            <div className="doc" dangerouslySetInnerHTML={{ __html: state.documents?.[0]?.html ?? "" }} />
            <p style={{ marginTop: "1rem" }}>
              <button
                type="button"
                onClick={async () => {
                  try {
                    const s = await api<ParticipantState>("/api/participant/start", {
                      method: "POST",
                      body: JSON.stringify({
                        client_time: new Date().toISOString(),
                        tab_visible: visible,
                      }),
                    });
                    applyState(s);
                  } catch (e: any) {
                    setError(e.message);
                  }
                }}
                disabled={!visible}
              >
                {t("start")}
              </button>
            </p>
            {error && <p style={{ color: "crimson" }}>{error}</p>}
          </div>
        )}

        {readingPhase && (
          <div>
            <div className={showDocNav ? "reading-with-nav" : undefined}>
              {showDocNav && (
                <nav className="doc-nav" aria-label={t("caseInformation")}>
                  <div className="doc-nav-header">
                    <div className="doc-nav-title">{t("caseInformation")}</div>
                    <div className="doc-nav-count">
                      {safeDocIndex + 1} of {docs.length}
                    </div>
                    <div className="doc-nav-actions">
                      <button
                        type="button"
                        className="secondary"
                        disabled={safeDocIndex <= 0}
                        onClick={() => {
                          const next = Math.max(0, safeDocIndex - 1);
                          goToDoc(next, "previous");
                        }}
                      >
                        {t("previous")}
                      </button>
                      <button
                        type="button"
                        className="secondary"
                        disabled={safeDocIndex >= docs.length - 1}
                        onClick={() => {
                          const next = Math.min(docs.length - 1, safeDocIndex + 1);
                          goToDoc(next, "next");
                        }}
                      >
                        {t("next")}
                      </button>
                    </div>
                  </div>
                  <ol className="doc-nav-list">
                    {docs.map((d, i) => (
                      <li key={d.id}>
                        <button
                          type="button"
                          ref={i === safeDocIndex ? currentDocNavRef : undefined}
                          className={`doc-nav-item${i === safeDocIndex ? " current" : ""}`}
                          aria-current={i === safeDocIndex ? "true" : undefined}
                          onClick={() => {
                            goToDoc(i, "list");
                          }}
                        >
                          {d.title}
                        </button>
                      </li>
                    ))}
                  </ol>
                </nav>
              )}
              <div className={showDocNav ? "reading-main" : undefined}>
              {state.frame_html && (
                <div className="phase-frame" dangerouslySetInnerHTML={{ __html: state.frame_html }} />
              )}
              {activeDoc && (
                <div
                  className="doc"
                  ref={docElRef}
                  dangerouslySetInnerHTML={{ __html: activeDoc.html }}
                />
              )}
              </div>
            </div>
            {remaining === 0 && advanceStuck && (
              <div style={{ marginTop: "1rem" }}>
                <p style={{ color: "crimson" }}>{t("advanceStuck")}</p>
                <button
                  type="button"
                  onClick={() => {
                    setAdvanceStuck(false);
                    if (advanceAbortRef.current) return;
                    const ctl = { cancelled: false };
                    advanceAbortRef.current = ctl;
                    void advanceReading(ctl, state.phase).finally(() => {
                      if (advanceAbortRef.current === ctl) advanceAbortRef.current = null;
                    });
                  }}
                >
                  {t("continue")}
                </button>
              </div>
            )}
          </div>
        )}

        {state.decision && (
          <div>
            {(() => {
              const prompt = splitDecisionPrompt(state.decision.prompt_html);
              const previous = state.decision.show_previous ? state.decision.previous : null;
              const previousText =
                previous == null
                  ? ""
                  : previous.share_a != null
                    ? `${state.decision.option_a} ${previous.share_a}% · ${state.decision.option_b} ${100 - previous.share_a}%`
                    : `${state.decision.option_a} —% · ${state.decision.option_b} —%`;
              return (
                <>
                  {prompt.notice[0] && (
                    <p className="decision-title" dangerouslySetInnerHTML={{ __html: prompt.notice[0] }} />
                  )}
                  {previous && (
                    <p className="decision-previous">
                      {t("previousAnswer")}: {previousText}
                    </p>
                  )}
                  {prompt.slider && (
                    <p className="decision-step decision-follow" dangerouslySetInnerHTML={{ __html: prompt.slider }} />
                  )}
                  <BlankSlider
                    value={share}
                    touched={touched}
                    optionA={state.decision.option_a}
                    optionB={state.decision.option_b}
                    onChange={(v) => {
                      setShare(v);
                      setTouched(true);
                      setSubmitHint("");
                      void queueEvent("slider_change", { share_a: v }, state.phase);
                    }}
                  />
                  <label className="decision-field decision-follow">
                    {prompt.note ? (
                      <span className="decision-step" dangerouslySetInnerHTML={{ __html: prompt.note }} />
                    ) : (
                      <span className="decision-step">{t("justification")}</span>
                    )}
                    <textarea
                      value={justification}
                      maxLength={state.decision.max_chars}
                      onChange={(e) => {
                        setJustification(e.target.value);
                        setRevision((r) => r + 1);
                        setSubmitHint("");
                      }}
                      onKeyDown={(e) => {
                        // Enter inserts a newline only — never submit or skip the confirm step
                        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                          e.preventDefault();
                        }
                      }}
                      onPaste={(e) => {
                        const text = e.clipboardData.getData("text");
                        void queueEvent("draft_paste", { length: text.length }, state.phase);
                      }}
                    />
                    <small>
                      {justification.length} / {state.decision.max_chars} {t("chars")}
                    </small>
                  </label>
                  {prompt.confidence && (
                    <p
                      className="decision-step decision-follow"
                      dangerouslySetInnerHTML={{ __html: prompt.confidence }}
                    />
                  )}
                  <div style={{ margin: "0.35rem 0 0" }}>
                    <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap" }}>
                      {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                        <button
                          key={n}
                          type="button"
                          className={confidenceTouched && confidence === n ? undefined : "secondary"}
                          aria-pressed={confidenceTouched && confidence === n}
                          onClick={() => {
                            setConfidence(n);
                            setConfidenceTouched(true);
                            setRevision((r) => r + 1);
                            setSubmitHint("");
                            void queueEvent("confidence_change", { confidence: n }, state.phase);
                          }}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                    <div className="amounts" style={{ fontSize: "0.85rem", color: "var(--muted)" }}>
                      <span>{t("confidenceLow")}</span>
                      <span>{t("confidenceHigh")}</span>
                    </div>
                  </div>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "0.75rem",
                        alignItems: "center",
                        marginTop: "1rem",
                      }}
                    >
              {!confirmSubmit ? (
                <button type="button" onClick={tryShowConfirm}>
                  {t("submitDecision")}
                </button>
              ) : (
                <>
                  <span style={{ flex: "1 1 12rem", color: "var(--muted)", fontSize: "0.95rem" }}>
                    {t("submitIrreversible")}
                  </span>
                  <button type="button" onClick={tryConfirmSubmit}>
                    {t("confirmSubmit")}
                  </button>
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => {
                      setConfirmSubmit(false);
                      setSubmitHint("");
                      void queueEvent("decision_confirm", { action: "cancel" }, state.phase);
                    }}
                  >
                    {t("keepEditing")}
                  </button>
                </>
              )}
              {submitHint ? (
                <span
                  role="status"
                  style={{ flex: "1 1 100%", color: "crimson", fontSize: "0.95rem" }}
                >
                  {submitHint}
                </span>
              ) : null}
                    </div>
                </>
              );
            })()}
          </div>
        )}

        {state.phase === "survey" && (
          <SurveyForm
            answers={survey}
            audience={state.survey?.audience ?? "board"}
            onChange={(next) => {
              setSurvey(normalizeSurveyAnswers(next, state.survey?.audience ?? "board"));
              setSurveyRevision((r) => r + 1);
            }}
            onSubmit={() => void submitSurvey("explicit")}
          />
        )}

        {(state.phase === "end" || state.end) && !state.session_ended && (
          <div>
            <div className="doc" dangerouslySetInnerHTML={{ __html: state.end?.html ?? "" }} />
            <p style={{ marginTop: "1rem", color: "var(--muted)" }}>
              Contact: {state.end?.contact_email ?? state.contact_email ?? CONTACT_EMAIL}
            </p>
          </div>
        )}
      </div>
      <Footer
        code={state.phase === "consent" || state.public_code ? state.public_code : undefined}
        remaining={remaining}
      />
    </>
  );
}

function SurveyForm({
  answers,
  audience,
  onChange,
  onSubmit,
}: {
  answers: SurveyAnswers;
  audience: SurveyAudience;
  onChange: (a: SurveyAnswers) => void;
  onSubmit: () => void;
}) {
  const steps = surveySteps(answers);
  const [stepIndex, setStepIndex] = useState(() => resumeSurveyStep(answers, audience));
  const userEditedRef = useRef(false);
  const resumedRef = useRef(
    surveySteps(answers).some((s) => surveyStepAnswered(s, answers, audience)),
  );

  useEffect(() => {
    const nextLen = surveySteps(answers).length;
    setStepIndex((i) => Math.min(i, nextLen - 1));
  }, [answers.ai_board_use]);

  // Resume draft progress once when answers arrive from the server — not after the user picks.
  useEffect(() => {
    if (userEditedRef.current || resumedRef.current) return;
    const hasProgress = surveySteps(answers).some((s) => surveyStepAnswered(s, answers, audience));
    if (hasProgress) {
      setStepIndex(resumeSurveyStep(answers, audience));
      resumedRef.current = true;
    }
  }, [answers, audience]);

  const step = steps[Math.min(stepIndex, steps.length - 1)]!;
  const total = surveyQuestionCount(answers.ai_board_use);
  const isLast = stepIndex >= steps.length - 1;
  const canAdvance = surveyStepAnswered(step, answers, audience);

  const set = <K extends keyof SurveyAnswers>(key: K, value: SurveyAnswers[K]) => {
    userEditedRef.current = true;
    onChange({ ...answers, [key]: value });
  };

  const goNext = () => {
    if (!canAdvance) return;
    if (isLast) {
      if (surveyAnswersComplete(answers, audience)) onSubmit();
      return;
    }
    setStepIndex((i) => i + 1);
  };

  const goBack = () => {
    setStepIndex((i) => Math.max(0, i - 1));
  };

  return (
    <div>
      <h2>{t("surveyTitle")}</h2>
      <p style={{ color: "var(--muted)", marginTop: 0 }}>
        {stepIndex + 1} of {total}
      </p>

      {step === 1 && (
        <ScaleQuestion
          prompt="In general, how willing or unwilling are you to take risks?"
          low="0 = completely unwilling to take risks"
          high="10 = very willing to take risks"
          value={answers.risk_general}
          onPick={(n) => set("risk_general", n)}
        />
      )}
      {step === 2 && (
        <ScaleQuestion
          prompt="How willing are you to take risks in financial matters?"
          low="0 = completely unwilling to take risks"
          high="10 = very willing to take risks"
          value={answers.risk_financial}
          onPick={(n) => set("risk_financial", n)}
        />
      )}
      {step === 3 && audience === "consulting_partners" && (
        <ChoiceQuestion
          prompt="What unit or division do you belong to?"
          options={[
            { value: "tax_legal", label: "Tax and Legal" },
            { value: "audit", label: "Audit" },
            { value: "advisory", label: "Advisory" },
            { value: "other", label: "Other" },
          ]}
          value={answers.division}
          onPick={(v) => set("division", v as SurveyAnswers["division"])}
        />
      )}
      {step === 3 && audience !== "consulting_partners" && (
        <>
          <ChoiceQuestion
            prompt="Which best describes your current role?"
            options={[
              { value: "chair", label: "Chair" },
              { value: "board_member", label: "Board member" },
              { value: "executive", label: "Executive" },
              { value: "consultant", label: "Consultant" },
              { value: "other", label: "Other" },
            ]}
            value={answers.role}
            onPick={(v) => {
              const role = v as SurveyAnswers["role"];
              userEditedRef.current = true;
              onChange({
                ...answers,
                role,
                role_other: role === "other" ? answers.role_other : null,
              });
            }}
          />
          {answers.role === "other" && (
            <div style={{ marginTop: "0.75rem" }}>
              <label style={{ display: "block", fontWeight: 600, marginBottom: "0.35rem" }}>
                Please specify
              </label>
              <input
                type="text"
                maxLength={200}
                value={answers.role_other ?? ""}
                onChange={(e) => set("role_other", e.target.value)}
                style={{ display: "block", width: "100%", maxWidth: "28rem", padding: "0.6rem", font: "inherit" }}
              />
            </div>
          )}
        </>
      )}
      {step === 4 && (
        <NumberQuestion
          prompt="For how many years have you taken part in strategic decisions for a business or organisation? Include every role, including earlier ones."
          hint="Number of years (0–80)."
          min={0}
          max={80}
          value={answers.years_strategic}
          onChange={(n) => set("years_strategic", n)}
        />
      )}
      {step === 5 && (
        <NumberQuestion
          prompt="How old are you?"
          hint="Number of years (18–100)."
          min={18}
          max={100}
          value={answers.age}
          onChange={(n) => set("age", n)}
        />
      )}
      {step === 6 && (
        <ChoiceQuestion
          prompt="How would you describe your gender?"
          options={[
            { value: "female", label: "Female" },
            { value: "male", label: "Male" },
            { value: "other", label: "Other" },
            { value: "prefer_not_to_say", label: "Prefer not to say" },
          ]}
          value={answers.gender}
          onPick={(v) => set("gender", v as SurveyAnswers["gender"])}
        />
      )}
      {step === 7 && (
        <ChoiceQuestion
          prompt={aiUseCopy(audience).prompt}
          stacked
          options={aiUseCopy(audience).grades}
          value={answers.ai_board_use}
          onPick={(v) => set("ai_board_use", v as SurveyAnswers["ai_board_use"])}
        />
      )}
      {step === 8 && (
        <AiUseQuestion
          audience={audience}
          answers={answers}
          onChange={(next) => {
            userEditedRef.current = true;
            onChange(next);
          }}
        />
      )}
      {step === 11 && (
        <ScaleQuestion
          prompt={
            audience === "consulting_partners"
              ? "In general, how much do you trust an LLM-based AI tool from a frontier AI lab to summarize an information pack?"
              : "In general, how much do you trust an LLM-based AI tool from a frontier AI lab to summarize a board information pack?"
          }
          low="0 = do not trust at all"
          high="10 = trust completely"
          min={0}
          max={10}
          value={answers.trust_ai_board_pack}
          onPick={(n) => set("trust_ai_board_pack", n)}
        />
      )}
      {step === 12 && (
        <ScaleQuestion
          prompt="How much did you trust the AI brief as described in this case when you made your decision?"
          low="0 = did not trust it at all"
          high="10 = trusted it completely"
          min={0}
          max={10}
          value={answers.trust_ai_brief}
          onPick={(n) => set("trust_ai_brief", n)}
        />
      )}

      <p style={{ display: "flex", gap: "0.75rem", marginTop: "1.5rem", flexWrap: "wrap" }}>
        {stepIndex > 0 && (
          <button type="button" className="secondary" onClick={goBack}>
            {t("previous")}
          </button>
        )}
        <button type="button" disabled={!canAdvance} onClick={goNext}>
          {isLast ? t("surveySubmit") : t("next")}
        </button>
      </p>
    </div>
  );
}

type SurveyStep = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 11 | 12;

function surveySteps(answers: SurveyAnswers): SurveyStep[] {
  const base: SurveyStep[] = [1, 2, 3, 4, 5, 6, 7];
  const trust: SurveyStep[] = [11, 12];
  if (surveyShowsFollowUps(answers.ai_board_use)) {
    return [...base, 8, ...trust];
  }
  return [...base, ...trust];
}

function surveyStepAnswered(
  step: SurveyStep,
  answers: SurveyAnswers,
  audience: SurveyAudience = "board",
): boolean {
  switch (step) {
    case 1:
      return answers.risk_general != null;
    case 2:
      return answers.risk_financial != null;
    case 3:
      if (audience === "consulting_partners") return answers.division != null;
      if (answers.role == null) return false;
      if (answers.role === "other") {
        const other = (answers.role_other ?? "").trim();
        return other.length > 0 && other.length <= 200;
      }
      return true;
    case 4:
      return (
        answers.years_strategic != null &&
        Number.isInteger(answers.years_strategic) &&
        answers.years_strategic >= 0 &&
        answers.years_strategic <= 80
      );
    case 5:
      return (
        answers.age != null &&
        Number.isInteger(answers.age) &&
        answers.age >= 18 &&
        answers.age <= 100
      );
    case 6:
      return answers.gender != null;
    case 7:
      return answers.ai_board_use != null;
    case 8:
      return surveyAiUsesAnswered(answers);
    case 11:
      return answers.trust_ai_board_pack != null;
    case 12:
      return answers.trust_ai_brief != null;
  }
}

function resumeSurveyStep(answers: SurveyAnswers, audience: SurveyAudience = "board"): number {
  const steps = surveySteps(answers);
  const idx = steps.findIndex((s) => !surveyStepAnswered(s, answers, audience));
  return idx < 0 ? steps.length - 1 : idx;
}

type AiUseKey = "ai_board_admin" | "ai_analysis" | "ai_monitor_outside" | "ai_use_none";

function toggleAiUse(answers: SurveyAnswers, key: AiUseKey): SurveyAnswers {
  if (key === "ai_use_none") {
    if (answers.ai_use_none === true) {
      return {
        ...answers,
        ai_board_admin: null,
        ai_analysis: null,
        ai_monitor_outside: null,
        ai_use_none: null,
      };
    }
    return {
      ...answers,
      ai_board_admin: false,
      ai_analysis: false,
      ai_monitor_outside: false,
      ai_use_none: true,
    };
  }
  const selected = {
    ai_board_admin: answers.ai_board_admin === true,
    ai_analysis: answers.ai_analysis === true,
    ai_monitor_outside: answers.ai_monitor_outside === true,
  };
  selected[key] = !selected[key];
  if (!selected.ai_board_admin && !selected.ai_analysis && !selected.ai_monitor_outside) {
    return {
      ...answers,
      ai_board_admin: null,
      ai_analysis: null,
      ai_monitor_outside: null,
      ai_use_none: null,
    };
  }
  return {
    ...answers,
    ...selected,
    ai_use_none: false,
  };
}

function aiUseCopy(audience: SurveyAudience): {
  prompt: string;
  grades: { value: string; label: string }[];
  followPrompt: string;
  preparation: string;
} {
  if (audience === "consulting_partners") {
    return {
      prompt:
        "Do you use LLM-based AI tools in your current role, such as ChatGPT, Claude, Gemini, or Copilot? Which statement describes your use best?",
      grades: [
        { value: "no_no_plans", label: "No, and I have no plans to use LLM-based AI tools in my current role" },
        { value: "no_interested", label: "No, but I am interested in using LLM-based AI tools" },
        { value: "yes_experimented", label: "Yes, but I have only experimented with LLM-based AI tools" },
        { value: "yes_sometimes", label: "Yes, I use LLM-based AI tools sometimes in my current role" },
        { value: "yes_often", label: "Yes, I often use LLM-based AI tools in my current role" },
        { value: "not_applicable", label: "Not applicable — this does not apply to my current role" },
      ],
      followPrompt: "What do you use LLM-based AI tools for in your current role? Tick any that apply.",
      preparation:
        "Preparation and admin: reading or summarising material, reports, agendas, minutes, notes, or drafts",
    };
  }
  return {
    prompt:
      "Do you use LLM-based AI tools in your board work, such as ChatGPT, Claude, Gemini, or Copilot? Which statement describes your use best?",
    grades: [
      { value: "no_no_plans", label: "No, and I have no plans to use LLM-based AI tools for board work" },
      { value: "no_interested", label: "No, but I am interested in using LLM-based AI tools" },
      { value: "yes_experimented", label: "Yes, but I have only experimented with LLM-based AI tools" },
      { value: "yes_sometimes", label: "Yes, I use LLM-based AI tools sometimes in my board work" },
      { value: "yes_often", label: "Yes, I often use LLM-based AI tools in my board work" },
      { value: "not_applicable", label: "Not applicable — I do not do board work" },
    ],
    followPrompt: "What do you use LLM-based AI tools for in your board work? Tick any that apply.",
    preparation:
      "Preparation and admin: reading or summarising board papers, agendas, minutes, notes, or drafts",
  };
}

function AiUseQuestion({
  audience,
  answers,
  onChange,
}: {
  audience: SurveyAudience;
  answers: SurveyAnswers;
  onChange: (a: SurveyAnswers) => void;
}) {
  const copy = aiUseCopy(audience);
  const options: { key: AiUseKey; label: string }[] = [
    { key: "ai_board_admin", label: copy.preparation },
    {
      key: "ai_analysis",
      label:
        "Analysis: accounts, numbers, risks, or scenarios in the material you already have",
    },
    {
      key: "ai_monitor_outside",
      label: "External intelligence: industry news, competitors, regulation, or sustainability",
    },
    { key: "ai_use_none", label: "None of these" },
  ];
  return (
    <div style={{ margin: "1.25rem 0" }}>
      <div style={{ fontWeight: 600, marginBottom: "0.75rem" }}>{copy.followPrompt}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", alignItems: "stretch" }}>
        {options.map((o) => {
          const pressed = answers[o.key] === true;
          return (
            <button
              key={o.key}
              type="button"
              className={pressed ? undefined : "secondary"}
              aria-pressed={pressed}
              onClick={() => onChange(toggleAiUse(answers, o.key))}
              style={{ textAlign: "left", width: "100%" }}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ScaleQuestion({
  prompt,
  low,
  high,
  value,
  onPick,
  min = 0,
  max = 10,
}: {
  prompt: string;
  low: string;
  high: string;
  value: number | null;
  onPick: (n: number) => void;
  min?: number;
  max?: number;
}) {
  const points = Array.from({ length: max - min + 1 }, (_, i) => min + i);
  return (
    <div style={{ margin: "1.25rem 0" }}>
      <div style={{ fontWeight: 600, marginBottom: "0.75rem" }}>{prompt}</div>
      <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap" }}>
        {points.map((n) => (
          <button
            key={n}
            type="button"
            className={value === n ? undefined : "secondary"}
            aria-pressed={value === n}
            onClick={() => onPick(n)}
          >
            {n}
          </button>
        ))}
      </div>
      <div className="amounts" style={{ fontSize: "0.85rem", color: "var(--muted)" }}>
        <span>{low}</span>
        <span>{high}</span>
      </div>
    </div>
  );
}

function ChoiceQuestion({
  prompt,
  options,
  value,
  onPick,
  stacked,
}: {
  prompt: string;
  options: { value: string; label: string }[];
  value: string | null;
  onPick: (v: string) => void;
  stacked?: boolean;
}) {
  return (
    <div style={{ margin: "1.25rem 0" }}>
      <div style={{ fontWeight: 600, marginBottom: "0.75rem" }}>{prompt}</div>
      <div
        style={{
          display: "flex",
          flexDirection: stacked ? "column" : "column",
          gap: "0.5rem",
          alignItems: "stretch",
        }}
      >
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            className={value === o.value ? undefined : "secondary"}
            aria-pressed={value === o.value}
            onClick={() => onPick(o.value)}
            style={{ textAlign: "left", width: "100%" }}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function NumberQuestion({
  prompt,
  hint,
  min,
  max,
  value,
  onChange,
}: {
  prompt: string;
  hint: string;
  min: number;
  max: number;
  value: number | null;
  onChange: (n: number | null) => void;
}) {
  return (
    <div style={{ margin: "1.25rem 0" }}>
      <div style={{ fontWeight: 600, marginBottom: "0.75rem" }}>{prompt}</div>
      <p style={{ color: "var(--muted)", fontSize: "0.9rem", marginTop: 0 }}>{hint}</p>
      <input
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        step={1}
        value={value ?? ""}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === "") {
            onChange(null);
            return;
          }
          const n = Number(raw);
          if (!Number.isFinite(n)) {
            onChange(null);
            return;
          }
          onChange(Math.trunc(n));
        }}
        style={{ display: "block", width: "100%", maxWidth: "12rem", padding: "0.6rem", font: "inherit" }}
      />
    </div>
  );
}

const WorkshopApp = lazy(() =>
  import("./workshop/WorkshopPage").then((m) => ({ default: m.WorkshopApp })),
);

function WorkshopRoute() {
  return (
    <Suspense fallback={<div className="shell">Loading…</div>}>
      <WorkshopApp />
    </Suspense>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/join" element={<JoinPage />} />
      <Route path="/r/:token" element={<LinkJoin />} />
      <Route path="/p" element={<Experiment />} />
      <Route path="/w" element={<Navigate to="/join" replace />} />
      <Route path="/w/:token" element={<WorkshopRoute />} />
      <Route path="/" element={<JoinPage />} />
    </Routes>
  );
}
